import { Copy, Mail } from 'lucide-react';
import { Fragment, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { type UserEmailRecipient, useUserEmailRecipients } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
  Textarea,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import { DEFAULT_GROUP_ORDER } from '@/shared/utils/permissions';

type DistributionList = {
  id: string;
  label: string;
  emails: string[];
};

type DistributionSection = {
  id: string;
  label: string;
  lists: DistributionList[];
};

const groupOrderIndex = new Map<string, number>(
  DEFAULT_GROUP_ORDER.map((key, index) => [key, index])
);

const sortByGroupOrder = (left: string, right: string) => {
  const leftIndex = groupOrderIndex.get(left) ?? Number.POSITIVE_INFINITY;
  const rightIndex = groupOrderIndex.get(right) ?? Number.POSITIVE_INFINITY;
  if (leftIndex !== rightIndex) return leftIndex - rightIndex;
  return left.localeCompare(right);
};

/**
 * Falls back to the raw group key when a group has no translation yet, so a
 * custom group added later still shows up with a readable name.
 */
const getGroupLabel = (t: ReturnType<typeof useIntl>['t'], key: string) => {
  const labelKey = `portalRoles.${key}`;
  const label = t(labelKey);
  return label === labelKey ? key : label;
};

export function EmailDistribution() {
  const { t, tc } = useIntl();
  const { data, isLoading } = useUserEmailRecipients();
  const recipients = useMemo<UserEmailRecipient[]>(() => data?.recipients ?? [], [data]);

  const sections = useMemo<DistributionSection[]>(() => {
    const byDomain = new Map<string, string[]>();
    const byGroup = new Map<string, string[]>();

    for (const recipient of recipients) {
      if (recipient.groupDomain) {
        const emails = byDomain.get(recipient.groupDomain) ?? [];
        emails.push(recipient.email);
        byDomain.set(recipient.groupDomain, emails);
      }
      if (recipient.groupKey) {
        const emails = byGroup.get(recipient.groupKey) ?? [];
        emails.push(recipient.email);
        byGroup.set(recipient.groupKey, emails);
      }
    }

    const domainLists = (['gmn', 'customer'] as const)
      .filter((domain) => byDomain.has(domain))
      .map((domain) => ({
        id: `domain:${domain}`,
        label: t(
          domain === 'gmn'
            ? 'emailDistribution.lists.domainGmn'
            : 'emailDistribution.lists.domainCustomer'
        ),
        emails: byDomain.get(domain) ?? [],
      }));

    const groupLists = [...byGroup.keys()].sort(sortByGroupOrder).map((groupKey) => ({
      id: `group:${groupKey}`,
      label: getGroupLabel(t, groupKey),
      emails: byGroup.get(groupKey) ?? [],
    }));

    return [
      {
        id: 'all',
        label: t('emailDistribution.sections.all'),
        lists: [
          {
            id: 'all',
            label: t('emailDistribution.lists.all'),
            emails: recipients.map((recipient) => recipient.email),
          },
        ],
      },
      {
        id: 'domains',
        label: t('emailDistribution.sections.byDomain'),
        lists: domainLists,
      },
      {
        id: 'groups',
        label: t('emailDistribution.sections.byGroup'),
        lists: groupLists,
      },
    ].filter((section) => section.lists.length > 0);
  }, [recipients, t]);

  const allLists = useMemo(() => sections.flatMap((section) => section.lists), [sections]);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedListId = searchParams.get('list');

  const selectedList = useMemo(() => {
    if (allLists.length === 0) return null;
    if (!selectedListId) return allLists[0];
    return allLists.find((list) => list.id === selectedListId) ?? allLists[0];
  }, [allLists, selectedListId]);

  useEffect(() => {
    if (allLists.length === 0) return;
    if (selectedListId) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('list', allLists[0].id);
    setSearchParams(nextParams, { replace: true });
  }, [allLists, searchParams, selectedListId, setSearchParams]);

  const selectList = (listId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('list', listId);
    setSearchParams(nextParams, { replace: true });
  };

  const addressLine = selectedList ? selectedList.emails.join(', ') : '';

  const handleCopy = async () => {
    if (!selectedList || selectedList.emails.length === 0) return;

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard unavailable');
      }
      await navigator.clipboard.writeText(addressLine);
      toast.success(tc('emailDistribution.copySuccess', selectedList.emails.length));
    } catch (_error) {
      toast.error(t('emailDistribution.copyError'));
    }
  };

  return (
    <PageContainer>
      <PageTitle
        icon={Mail}
        breadcrumb={
          <>
            <SimpleBreadcrumbItem>{t('navigation.items.gmnSettings')}</SimpleBreadcrumbItem>
            <SimpleBreadcrumbItem isLast>{t('emailDistribution.title')}</SimpleBreadcrumbItem>
          </>
        }
        title={t('emailDistribution.title')}
        subtitle={t('emailDistribution.subtitle')}
      />

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>{t('emailDistribution.listTitle')}</CardTitle>
            <CardDescription>{t('emailDistribution.listDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHead>{t('emailDistribution.list')}</TableHead>
                  <TableHead className="text-right">{t('emailDistribution.recipients')}</TableHead>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {allLists.length === 0 && !isLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      className="text-muted-foreground text-sm py-6 text-center"
                    >
                      {t('emailDistribution.empty')}
                    </TableCell>
                  </TableRow>
                )}
                {sections.map((section) => (
                  <Fragment key={section.id}>
                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                      <TableCell
                        colSpan={2}
                        className="py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                      >
                        {section.label}
                      </TableCell>
                    </TableRow>
                    {section.lists.map((list) => (
                      <TableRow
                        key={list.id}
                        onClick={() => selectList(list.id)}
                        className={cn(
                          'cursor-pointer',
                          selectedList?.id === list.id && 'bg-muted/40'
                        )}
                      >
                        <TableCell className="font-medium">{list.label}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {list.emails.length}
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{selectedList?.label ?? t('emailDistribution.detailsTitle')}</CardTitle>
            <CardDescription>{t('emailDistribution.detailsDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selectedList ? (
              <div className="text-sm text-muted-foreground">
                {isLoading ? t('emailDistribution.loading') : t('emailDistribution.empty')}
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <span className="text-sm text-muted-foreground">
                    {tc('emailDistribution.recipientCount', selectedList.emails.length)}
                  </span>
                  <Button
                    onClick={handleCopy}
                    disabled={selectedList.emails.length === 0}
                    className="flex items-center gap-2 shadow-sm"
                  >
                    <Copy className="size-4" />
                    {t('emailDistribution.copy')}
                  </Button>
                </div>
                <Textarea
                  readOnly
                  value={addressLine}
                  rows={12}
                  aria-label={t('emailDistribution.addressesLabel')}
                  className="font-mono text-xs"
                  onFocus={(event) => event.currentTarget.select()}
                />
                <p className="text-xs text-muted-foreground">{t('emailDistribution.hint')}</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
